import React, { useState, useEffect, useReducer, useContext, useMemo, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        background: theme.mode === "light"
            ? "linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(248,250,252,0.9) 100%)"
            : "linear-gradient(180deg, rgba(15,23,42,0.45) 0%, rgba(2,6,23,0.58) 100%)",
    },
    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: `1px solid ${theme.mode === "light" ? "rgba(148,163,184,0.16)" : "rgba(255,255,255,0.06)"}`,
        padding: theme.spacing(1.25, 0, 1.5),
        background: theme.mode === "light"
            ? "linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(248,250,252,0.34) 100%)"
            : "linear-gradient(180deg, rgba(15,23,42,0.12) 0%, rgba(2,6,23,0.28) 100%)",
    },
    refreshingOverlay: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        display: "flex",
        justifyContent: "center",
        padding: "6px 0",
    },
    refreshingPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 999,
        background: theme.mode === "light"
            ? "rgba(255,255,255,0.88)"
            : "rgba(15,23,42,0.82)",
        border: `1px solid ${theme.mode === "light" ? "rgba(148,163,184,0.18)" : "rgba(255,255,255,0.08)"}`,
        color: theme.mode === "light" ? "rgb(71, 85, 105)" : "rgb(226, 232, 240)",
        fontSize: "12px",
        fontWeight: 600,
        boxShadow: theme.mode === "light" ? "0 10px 24px rgba(15,23,42,0.06)" : "0 10px 24px rgba(2,6,23,0.2)",
    },
    refreshingDots: {
        display: "inline-block",
        overflow: "hidden",
        width: 0,
        animation: "$dots 1s steps(3, end) infinite",
    },
    "@keyframes dots": {
        "0%": { width: 0 },
        "100%": { width: 12 },
    },
    ticketsListHeader: {
        color: theme.mode === "light" ? "rgb(51, 65, 85)" : "rgb(226, 232, 240)",
        zIndex: 2,
        background: theme.mode === "light"
            ? "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.92) 100%)"
            : "linear-gradient(180deg, rgba(15,23,42,0.88) 0%, rgba(2,6,23,0.9) 100%)",
        borderBottom: `1px solid ${theme.mode === "light" ? "rgba(148,163,184,0.16)" : "rgba(255,255,255,0.08)"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(1.25, 1.5),
        backdropFilter: "blur(14px)",
    },
    ticketsCount: {
        fontWeight: 600,
        color: theme.mode === "light" ? "rgb(100, 116, 139)" : "rgb(148, 163, 184)",
        marginLeft: "8px",
        fontSize: "13px",
        letterSpacing: ".02em",
    },
    noTicketsText: {
        textAlign: "center",
        color: theme.mode === "light" ? "rgb(100, 116, 139)" : "rgb(148, 163, 184)",
        fontSize: "14px",
        lineHeight: "1.6",
        maxWidth: 260,
    },
    noTicketsTitle: {
        textAlign: "center",
        fontSize: "18px",
        fontWeight: "700",
        margin: "0 0 8px",
        color: theme.mode === "light" ? "rgb(30, 41, 59)" : "rgb(248, 250, 252)",
    },
    noTicketsDiv: {
        display: "flex",
        margin: "48px 24px",
        padding: "28px 20px",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 24,
        background: theme.mode === "light"
            ? "rgba(255,255,255,0.86)"
            : "rgba(15,23,42,0.62)",
        border: `1px dashed ${theme.mode === "light" ? "rgba(148,163,184,0.3)" : "rgba(255,255,255,0.12)"}`,
        boxShadow: theme.mode === "light" ? "0 16px 36px rgba(15,23,42,0.05)" : "0 16px 36px rgba(2,6,23,0.2)",
    },
}));

const ticketSortAsc = (a, b) => {
    
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
   
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    state.unshift(state.splice(ticketIndex, 1)[0]);
                }
            } else {
                state.push(ticket);
            }
        });
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
        } else {
            state.unshift(ticket);
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
            state.unshift(state.splice(ticketIndex, 1)[0]);
        } else {
            if (action.status === action.payload.status) {
                state.unshift(ticket);
            }
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex].contact = contact;
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        withUnreadMessages,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        awaiting,
        refreshKey
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    const [isRefreshing, setIsRefreshing] = useState(false);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable';
    const companyId = user.companyId;

    useEffect(() => {
        dispatch({ type: "RESET" });
        setIsRefreshing(true);
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, withUnreadMessages, whatsappIds, statusFilter, sortTickets, searchOnMessages, awaiting, refreshKey]);

    const statusParam = status;

    const { tickets, hasMore, loading } = useTickets({
        pageNumber,
        searchParam,
        status: statusParam,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        withUnreadMessages,
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets,
        awaiting,
        refreshKey
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            if (!loading) {
                dispatch({
                    type: "LOAD_TICKETS",
                    payload: tickets,
                    status,
                    sortDir: sortTickets
                });
                if (isRefreshing) {
                    setIsRefreshing(false);
                }
            }
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets, companyId, status, sortTickets, isRefreshing, loading]);

    useEffect(() => {
        const matchesAwaiting = ticket => {
            if (!awaiting) return true;
            if (!ticket || ticket.isGroup) return false;
            if (awaiting === "agent") {
                return ticket.fromMe === true;
            }
            if (awaiting === "customer") {
                return ticket.fromMe === false;
            }
            return true;
        };

        const shouldUpdateTicket = ticket => {
            return (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
                ((!ticket?.queueId && showTicketWithoutQueue) || selectedQueueIds.indexOf(ticket?.queueId) > -1) &&
                matchesAwaiting(ticket);
            // (!blockNonDefaultConnections || (ticket.status == 'group' && ignoreUserConnectionForGroups) || !user?.whatsappId || ticket.whatsappId == user?.whatsappId);
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const shouldKeepTicketInCurrentList = (ticket) => {
            if (!ticket?.id) return false;
            if (status && status !== "search" && ticket.status !== status) return false;
            if (notBelongsToUserQueues(ticket)) return false;
            return shouldUpdateTicket(ticket);
        };

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // console.log(shouldUpdateTicket(data.ticket))
            if (data.action === "update") {
                if (shouldKeepTicketInCurrentList(data.ticket)) {
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets
                    });
                } else if (data.ticket?.id) {
                    dispatch({
                        type: "DELETE_TICKET",
                        payload: data.ticket.id,
                        status: status,
                        sortDir: sortTickets
                    });
                }
            }

            // else if (data.action === "update" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET",
            //         payload: data.ticket,
            //     });
            // }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });

            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            if (data.action === "create") {
                if (shouldKeepTicketInCurrentList(data.ticket)) {
                    dispatch({
                        type: "UPDATE_TICKET_UNREAD_MESSAGES",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets
                    });
                } else if (data.ticket?.id) {
                    dispatch({
                        type: "DELETE_TICKET",
                        payload: data.ticket.id,
                        status: status,
                        sortDir: sortTickets
                    });
                }
            }
            // else if (data.action === "create" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET_UNREAD_MESSAGES",
            //         payload: data.ticket,
            //     });
            // }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
        if (status) {
            socket.emit("joinTickets", status);
        } else {
            socket.emit("joinNotification");
        }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, showTicketWithoutQueue, awaiting]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleResetUnread = useCallback((ticketId) => {
        if (!ticketId) return;

        dispatch({
            type: "RESET_UNREAD",
            payload: ticketId,
            status,
            sortDir: sortTickets
        });
    }, [status, sortTickets]);

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

        if (status && status !== "search") {
        ticketsList = ticketsList.filter(ticket => ticket.status === status)
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                {(isRefreshing || loading) && (
                    <div className={classes.refreshingOverlay}>
                        <div className={classes.refreshingPill}>
                            Atualizando
                            <span className={classes.refreshingDots}>...</span>
                        </div>
                    </div>
                )}
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                // <List key={ticket.id}>
                                //     {console.log(ticket)}
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                    onResetUnread={handleResetUnread}
                                />
                                // </List>
                            ))}
                        </>
                    )}
                    {loading && ticketsList.length === 0 && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
